import client from "./client";

export type BillingPlanKey = string;

export type BillingPlanStatus =
  | "trialing"
  | "active"
  | "past_due"
  | "canceled"
  | "inactive";

export type BillingSupportTier = "docs" | "email" | "priority" | "white_glove";
export type BillingPlanChangeDirection = "upgrade" | "downgrade";
export type BillingPlanPriceLookupStatus =
  | "free"
  | "configured"
  | "not_configured"
  | "unavailable";

export interface BillingPlanLimits {
  sites: number;
  locales: number;
  seats: number;
  formSubmissions: number;
  pages: number | null;
  domains: number;
  allowedLanguages: string[];
  translationCharactersPerMonth: number;
  deploymentRetention: number;
  rollbackEnabled: boolean;
  analyticsEnabled: boolean;
  exportEnabled: boolean;
  scheduledExports: boolean;
  supportTier: BillingSupportTier;
  seoCrawlerMaxUrlsPerCrawl: number;
  psiAuditsPerMonth: number;
}

export interface BillingUsageTotals {
  sites: number;
  locales: number;
  seats: number;
  formSubmissions: number;
  pages: number;
  domains: number;
  translationCharactersThisMonth: number;
  psiAuditsThisMonth: number;
}

export interface PlanCatalogEntry {
  key: BillingPlanKey;
  name: string;
  description: string;
  isFree: boolean;
  checkoutEnabled: boolean;
  limits: BillingPlanLimits;
}

export interface BillingPlanPriceSummary {
  providerPriceId: string | null;
  amount: number | null;
  currency: string | null;
  interval: "day" | "week" | "month" | "year" | null;
  intervalCount: number | null;
  display: string;
  lookupStatus: BillingPlanPriceLookupStatus;
}

export interface BillingPlanSnapshot {
  planId: string;
  planKey: BillingPlanKey;
  planName: string;
  description: string;
  status: BillingPlanStatus;
  renewsAt: string | null;
  currentPeriodStart: string | null;
  gracePeriodEndsAt: string | null;
  limits: BillingPlanLimits;
  usage: BillingUsageTotals;
  provider: "stripe";
  providerCustomerId: string | null;
  providerSubscriptionId: string | null;
  cancelAtPeriodEnd: boolean;
  actions: {
    editingAllowed: boolean;
    publishingBlocked: boolean;
    publicSitesRemainLive: boolean;
    operatorOverrideAvailable: boolean;
    gracePeriodActive: boolean;
  };
  lastWebhookAt: string | null;
  source: "stripe" | "default_trial" | "free";
  subscriptionId: string | null;
  isFreePlan: boolean;
  pendingPlanChange: {
    planKey: BillingPlanKey;
    planName: string;
    direction: BillingPlanChangeDirection;
    effectiveAt: string;
    providerScheduleId: string | null;
  } | null;
}

export interface BillingCheckoutSession {
  provider: "stripe";
  sessionId: string;
  checkoutUrl: string;
  customerId: string;
  planKey: string;
}

export interface BillingPortalSession {
  provider: "stripe";
  customerId: string;
  portalUrl: string;
  planKey?: BillingPlanKey;
}

export interface BillingPlanCatalogEntry {
  key: BillingPlanKey;
  name: string;
  description: string;
  isFree: boolean;
  checkoutEnabled: boolean;
  limits: BillingPlanLimits;
  price: BillingPlanPriceSummary;
}

export async function getBillingPlan(
  tenantId: string,
): Promise<BillingPlanSnapshot> {
  const { data } = await client.get<BillingPlanSnapshot>(
    `/tenants/${tenantId}/billing/plan`,
  );
  return data;
}

export async function listBillingPlans(
  tenantId: string,
): Promise<BillingPlanCatalogEntry[]> {
  const { data } = await client.get<BillingPlanCatalogEntry[]>(
    `/tenants/${tenantId}/billing/plans`,
  );
  return data;
}

export async function createCheckoutSession(
  tenantId: string,
  payload: {
    planKey: string;
    successUrl: string;
    cancelUrl: string;
  },
): Promise<BillingCheckoutSession> {
  const { data } = await client.post<BillingCheckoutSession>(
    `/tenants/${tenantId}/billing/checkout-session`,
    payload,
  );
  return data;
}

export async function updateBillingPlan(
  tenantId: string,
  payload: { planKey: BillingPlanKey },
): Promise<BillingPlanSnapshot> {
  const { data } = await client.post<BillingPlanSnapshot>(
    `/tenants/${tenantId}/billing/subscription-plan`,
    payload,
  );
  return data;
}

export async function cancelPendingBillingPlanChange(
  tenantId: string,
): Promise<BillingPlanSnapshot> {
  const { data } = await client.post<BillingPlanSnapshot>(
    `/tenants/${tenantId}/billing/subscription-plan/cancel-pending`,
  );
  return data;
}

export async function createPortalSession(
  tenantId: string,
  payload: { returnUrl: string; targetPlanKey?: BillingPlanKey },
): Promise<BillingPortalSession> {
  const { data } = await client.post<BillingPortalSession>(
    `/tenants/${tenantId}/billing/portal-session`,
    payload,
  );
  return data;
}

export interface BillingInvoiceSummary {
  id: string;
  providerInvoiceId: string;
  status: string;
  amountDue: number;
  amountPaid: number;
  amountRemaining: number;
  currency: string;
  hostedInvoiceUrl: string | null;
  invoicePdfUrl: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  issuedAt: string | null;
}

export interface BillingSyncResponse {
  plan: BillingPlanSnapshot;
  invoices: BillingInvoiceSummary[];
  syncedAt: string;
}

export async function listBillingInvoices(
  tenantId: string,
): Promise<BillingInvoiceSummary[]> {
  const { data } = await client.get<{ invoices: BillingInvoiceSummary[] }>(
    `/tenants/${tenantId}/billing/invoices`,
  );
  return data.invoices;
}

export async function refreshBillingInvoices(
  tenantId: string,
): Promise<{ syncedCount: number; invoices: BillingInvoiceSummary[] }> {
  const { data } = await client.post<{
    syncedCount: number;
    invoices: BillingInvoiceSummary[];
  }>(`/tenants/${tenantId}/billing/invoices/refresh`);
  return data;
}

export async function syncBillingState(
  tenantId: string,
): Promise<BillingSyncResponse> {
  const { data } = await client.post<BillingSyncResponse>(
    `/tenants/${tenantId}/billing/sync`,
  );
  return data;
}
